import React, { useEffect, useMemo } from 'react'
import { type NodeProps, useReactFlow, useUpdateNodeInternals } from '@xyflow/react'
import { LoraModel } from '@/components/canvas/nodes/variants/LoraModel'
import type { GenieNodeState } from '@genieartinc/schemas'
import { NODE_DIMENSIONS } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'

export interface LoraModelNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

function LoraModelNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as LoraModelNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)
  const reactFlow = useReactFlow()
  const updateNodeInternals = useUpdateNodeInternals()

  // Force canonical width on any pre-existing LoraModel node whose persisted
  // style.width diverged. Height is auto (card sizes to content). Runs on mount.
  useEffect(() => {
    const targetW = NODE_DIMENSIONS.LoraModel.width
    reactFlow.setNodes((nodes) =>
      nodes.map((n) => {
        if (n.id !== id) return n
        if (Number(n.style?.width) === targetW && n.style?.height === undefined) return n
        const { height: _omit, ...restStyle } = n.style ?? {}
        return {
          ...n,
          style: { ...restStyle, width: targetW },
          width: targetW,
        }
      }),
    )
    const raf = requestAnimationFrame(() => updateNodeInternals(id))
    return () => cancelAnimationFrame(raf)
  }, [id, reactFlow, updateNodeInternals])

  const variantProps = useMemo(
    () => ({
      id,
      selected,
      onHoverChange,
      onStateChange,
      onSelect: () => {},
      state: (genieState || {}) as unknown as Record<string, unknown>,
    }),
    [id, genieState, selected, onHoverChange, onStateChange],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.LoraModel

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <LoraModel {...variantProps} />
    </>
  )
}

export const LoraModelNode = React.memo(
  LoraModelNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as LoraModelNodeData
    const nextData = nextProps.data as unknown as LoraModelNodeData
    const prev = prevData.state as unknown as Record<string, unknown> | undefined
    const next = nextData.state as unknown as Record<string, unknown> | undefined
    return (
      prev?.title === next?.title &&
      prev?.scale === next?.scale &&
      prev?.loraId === next?.loraId &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

LoraModelNode.displayName = 'LoraModelNode'
